import { XIcon } from '@heroicons/react/outline'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { SAVE_THE_DATE } from '../../constants/saveTheDate'
import { AddToCalendar } from './AddToCalendar'
import { CardContent } from './CardContent'
import { CARD_SHAPE } from './cardShape'

type Props = {
  isOpen: boolean
  // Called the moment the guest asks to go back: show the leaderboard now.
  onBack: () => void
  // Called once the card has tucked itself away behind it: unmount.
  onClosed: () => void
  // Opened from the leaderboard's button: the sealed envelope slides up into
  // place as the leaderboard fades out. Opening the link shows it instantly.
  handoff?: boolean
}

const HANDOFF_MS = 500
const CLOSE_MS = 320

// sealed  -> waiting for a tap
// opening -> one continuous, overlapping sequence (flap, rise, settle)
// open    -> card on screen
type Phase = 'sealed' | 'opening' | 'open'

// One shared timeline (ms). Steps overlap so it reads as a single motion.
const T = {
  flap: { start: 0, dur: 520 },
  rise: { start: 360, dur: 760 },
  settle: { start: 1000, dur: 760 },
  // Envelope slides down behind the card and off screen.
  slide: { start: 1040, dur: 1060 },
}
const TOTAL = 2100

const EASE = {
  flap: 'cubic-bezier(0.45, 0, 0.2, 1)', // ease-in-out, like a hinge
  out: 'cubic-bezier(0.22, 1, 0.36, 1)', // ease-out-quint
  settle: 'cubic-bezier(0.16, 1, 0.3, 1)', // ease-out-expo
  slide: 'cubic-bezier(0.65, 0, 0.35, 1)', // ease-in-out: eases off, then away
}

// Card positions (percent of the card's own size). The envelope is a bit
// narrower than the card (92%), so on phones it doesn't fill the screen.
const CARD_IN = 'translateY(23%) scale(0.54)'
const CARD_UP = 'translateY(-16%) scale(0.6)'
const CARD_OUT = 'translateY(0) scale(1)'

const PAPER = {
  back: '#E4D8C2',
  front: '#EFE5D3',
  flap: '#E9DECA',
  edge: 'rgba(68, 50, 30, 0.14)',
}

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Every step lasts until TOTAL (endDelay) so they all finish together.
const timing = (start: number, dur: number, easing: string) => ({
  delay: start,
  duration: dur,
  endDelay: TOTAL - start - dur,
  easing,
  fill: 'both' as FillMode,
})

// The payoff: an in-game Save the Date (no redirect). It arrives sealed in an
// envelope; a tap opens it, and the ✕ (or Esc) returns to the leaderboard.
export const SaveTheDate = ({
  isOpen,
  onBack,
  onClosed,
  handoff = false,
}: Props) => {
  const stageRef = useRef<HTMLDivElement>(null)
  const slotRef = useRef<HTMLDivElement>(null)

  // Hand-off entrance. Layout effect so it starts before the first paint (no
  // frame of the envelope already in place).
  useLayoutEffect(() => {
    if (!isOpen || !handoff) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const frames = [
      { transform: 'translateY(56px)', opacity: 0 },
      { transform: 'translateY(0)', opacity: 1 },
    ]
    const timing = {
      duration: HANDOFF_MS,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'backwards' as FillMode,
    }
    const a = stageRef.current?.animate(frames, timing)
    const b = slotRef.current?.animate(frames, { ...timing, delay: 60 })
    return () => {
      a?.cancel()
      b?.cancel()
    }
  }, [isOpen, handoff])

  const [phase, setPhase] = useState<Phase>('sealed')
  const cardRef = useRef<HTMLElement>(null)
  const flapRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLDivElement>(null)
  const frontRef = useRef<SVGSVGElement>(null)
  const anims = useRef<Animation[]>([])

  // Fresh, sealed envelope every time it opens.
  useEffect(() => {
    if (isOpen) {
      setPhase('sealed')
      anims.current = []
    }
  }, [isOpen])

  const open = () => {
    if (phase !== 'sealed') return
    const card = cardRef.current
    const flap = flapRef.current
    const back = backRef.current
    const front = frontRef.current
    if (!card || !flap || !back || !front) return

    // Uses the separate `translate` property so it can run alongside the
    // flap's own `transform` (its hinge rotation).
    const slide = [{ translate: '0 0' }, { translate: '0 110vh' }]
    anims.current = [
      // Flap swings up; once past vertical it tucks behind the card.
      flap.animate(
        [
          { transform: 'perspective(900px) rotateX(0deg)', zIndex: 4 },
          {
            transform: 'perspective(900px) rotateX(90deg)',
            zIndex: 0,
            offset: 0.5,
          },
          { transform: 'perspective(900px) rotateX(180deg)', zIndex: 0 },
        ],
        timing(T.flap.start, T.flap.dur, EASE.flap)
      ),
      // Card slides up out of the pocket (still behind the pocket front).
      card.animate(
        [{ transform: CARD_IN }, { transform: CARD_UP }],
        timing(T.rise.start, T.rise.dur, EASE.out)
      ),
      // Card comes forward and grows to full size.
      card.animate(
        [
          { transform: CARD_UP, zIndex: 5 },
          { transform: CARD_OUT, zIndex: 5 },
        ],
        // 'forwards' only: before it starts, the rise animation owns the
        // card's position (both animate transform).
        {
          ...timing(T.settle.start, T.settle.dur, EASE.settle),
          fill: 'forwards',
        }
      ),
      // Once the card is in front, the whole envelope slides down behind it
      // and off screen.
      ...[back, front, flap].map((el) =>
        el.animate(slide, timing(T.slide.start, T.slide.dur, EASE.slide))
      ),
    ]
    setPhase('opening')
    if (prefersReducedMotion()) anims.current.forEach((a) => a.finish())
    void Promise.all(anims.current.map((a) => a.finished)).then(() =>
      setPhase((p) => (p === 'opening' ? 'open' : p))
    )
  }

  // Once open, drop the compositor layer: the card was rasterised while
  // small inside the envelope, and keeping that layer would leave the
  // border and lines slightly soft at full size.
  useEffect(() => {
    if (phase !== 'open') return
    const card = cardRef.current
    if (!card) return
    card.getAnimations().forEach((a) => {
      try {
        a.commitStyles()
      } catch {}
      a.cancel()
    })
    card.style.transform = 'none'
    card.style.willChange = 'auto'
  }, [phase])

  // Close: the leaderboard comes up (its backdrop at full strength, so the
  // darkness never changes; its panel easing in) while the card sinks and
  // fades beneath it. Mirrors the hand-off, a little quicker.
  // `closing`: animation started. `under`: the leaderboard is on screen, so
  // the card can drop beneath it and give up its own backdrop. (Dropping
  // before the leaderboard has mounted would flash the bare board.)
  const [closing, setClosing] = useState(false)
  const [under, setUnder] = useState(false)
  const close = useCallback(() => {
    if (closing) return
    setClosing(true)
    onBack()
    const t0 = performance.now()
    const waitForLeaderboard = () => {
      const up = document.querySelector('[role="dialog"] .min-h-screen')
      if (up || performance.now() - t0 > 300) setUnder(true)
      else requestAnimationFrame(waitForLeaderboard)
    }
    requestAnimationFrame(waitForLeaderboard)

    const done = () => onClosed()
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.setTimeout(done, 150)
      return
    }
    const frames = [
      { transform: 'translateY(0)', opacity: 1 },
      { transform: 'translateY(28px)', opacity: 0 },
    ]
    const timing = {
      duration: CLOSE_MS,
      easing: 'cubic-bezier(0.4, 0, 1, 1)', // ease-in: drifts, then away
      fill: 'forwards' as FillMode,
    }
    slotRef.current?.animate(frames, { ...timing, duration: CLOSE_MS * 0.7 })
    const a = stageRef.current?.animate(frames, timing)
    if (a) a.finished.then(done, done)
    else done()
  }, [closing, onBack, onClosed])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, close])

  if (!isOpen) return null
  const s = SAVE_THE_DATE
  const sealed = phase === 'sealed'

  return (
    <div
      className={`fixed inset-0 flex flex-col items-center justify-center overflow-x-hidden overscroll-contain px-4 pt-12 pb-6 sm:py-6 ${
        // No scrollbar flicker while the envelope slides off screen.
        phase === 'opening' || closing ? 'overflow-y-hidden' : 'overflow-y-auto'
      } ${
        // Closing: tuck under the leaderboard (z-10), whose backdrop takes over.
        under ? 'pointer-events-none z-[5]' : 'z-40'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={`Save the date: ${s.partnerOne} and ${s.partnerTwo}, ${s.date}, ${s.place}`}
    >
      {/* Backdrop: dark and softly blurred, so the game behind doesn't
          compete with the card. On close it fades out under the
          leaderboard's own backdrop. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none fixed inset-0 bg-black/[0.85] backdrop-blur-sm transition-opacity duration-300 ${
          under ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Stage: same footprint as the finished card. Its width leaves room
          for exactly what surrounds it: the padding, the ✕ above it on phones
          (beside it on wider screens), the gap and the buttons (two rows on
          phones, one row wider). */}
      <div
        ref={stageRef}
        className="relative aspect-[4/5] w-[min(92vw,calc((100dvh-12rem)*0.8),38rem)] shrink-0 sm:w-[min(calc(100vw-8rem),calc((100dvh-7.5rem)*0.8),38rem)]"
      >
        {/* Close: beside the card's top-right corner (above it on phones). */}
        <button
          type="button"
          onClick={close}
          className={`absolute -right-1 z-10 rounded-full p-1 ${
            // sealed: by the envelope (which starts 46% down the stage).
            // Phones: just above the card's corner; wider: beside it.
            sealed
              ? 'top-[calc(46%-2.75rem)] sm:right-[calc(4%-3rem)] sm:top-[46%]'
              : '-top-11 sm:-right-12 sm:top-0'
          } text-gray-300 transition-[top] duration-700 ease-out hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white`}
          aria-label="Back to leaderboard"
        >
          <XIcon className="h-7 w-7" />
        </button>
        {/* Envelope pieces never take clicks: once the envelope has dropped
            away it's invisible but would still sit over the page.
            Taps while sealed go to the button below. */}
        {/* Envelope back */}
        <div
          ref={backRef}
          aria-hidden="true"
          className={`pointer-events-none absolute left-[4%] right-[4%] top-[46%] bottom-0 rounded-md shadow-2xl will-change-transform ${
            phase === 'open' ? 'hidden' : ''
          }`}
          style={{ background: PAPER.back, zIndex: 1 }}
        />

        {/* The card (starts tucked inside the envelope): the Canva design
            with the photos in its photo spots. */}
        <article
          ref={cardRef}
          className="absolute inset-0 text-stone-900 will-change-transform"
          style={{
            transform: CARD_IN,
            zIndex: 2,
            // The shadow follows the card's hand-drawn edge.
            filter: 'drop-shadow(0 18px 28px rgba(0, 0, 0, 0.45))',
          }}
          aria-hidden={sealed}
        >
          {/* Cut to the design's hand-drawn green border, so the card's
              edge wobbles with it instead of being a perfect rectangle. */}
          <div
            className="absolute inset-0 bg-[#F2F1EB]"
            // A vector clip (not a mask image), so the edge stays sharp.
            style={{ clipPath: 'url(#std-card-clip)' }}
          >
            <svg aria-hidden="true" width="0" height="0" className="absolute">
              <clipPath id="std-card-clip" clipPathUnits="objectBoundingBox">
                <path
                  d={CARD_SHAPE}
                  transform="scale(0.000925926 0.000740741)"
                />
              </clipPath>
            </svg>
            <CardContent />
          </div>
          {/* The same words as real text, for screen readers. */}
          <div className="sr-only">
            <h2>{s.title}</h2>
            <p>{s.headline}</p>
            <p>
              {s.partnerOne} &amp; {s.partnerTwo}
            </p>
            <p>Friday, {s.date}</p>
            <p>{s.place}</p>
            <p>{s.footer}</p>
          </div>
        </article>

        {/* Envelope pocket (front) */}
        <svg
          ref={frontRef}
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className={`pointer-events-none absolute left-[4%] top-[46%] h-[54%] w-[92%] will-change-transform ${
            phase === 'open' ? 'hidden' : ''
          }`}
          style={{ zIndex: 3 }}
        >
          <polygon
            points="0,0 52,56 0,100"
            fill={PAPER.front}
            stroke={PAPER.edge}
            strokeWidth="0.4"
          />
          <polygon
            points="100,0 48,56 100,100"
            fill={PAPER.front}
            stroke={PAPER.edge}
            strokeWidth="0.4"
          />
          <polygon
            points="0,100 50,46 100,100"
            fill={PAPER.front}
            stroke={PAPER.edge}
            strokeWidth="0.4"
          />
        </svg>

        {/* Envelope flap, hinged on the envelope's top edge */}
        <div
          ref={flapRef}
          aria-hidden="true"
          className={`pointer-events-none absolute left-[4%] right-[4%] top-[46%] h-[32%] will-change-transform ${
            phase === 'open' ? 'hidden' : ''
          }`}
          style={{
            transformOrigin: 'top center',
            transform: 'perspective(900px) rotateX(0deg)',
            zIndex: 4,
          }}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="h-full w-full"
          >
            <polygon
              points="0,0 100,0 50,100"
              fill={PAPER.flap}
              stroke={PAPER.edge}
              strokeWidth="0.4"
            />
            {/* a fine green line inside the flap, in the card's green */}
            <polygon
              points="6,0 94,0 50,88"
              fill="none"
              stroke="#0D7B48"
              strokeOpacity="0.55"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>

        {/* Whole envelope is the tap target while sealed */}
        {sealed && (
          <button
            type="button"
            onClick={open}
            className="absolute inset-x-[4%] top-[46%] bottom-0 z-[6] rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Open your Save the Date"
          />
        )}
      </div>

      {/* One fixed-height slot under the stage, shared by "Tap to open" and
          the calendar button, so swapping them never shifts the card. It sits
          above the stage (z-10) so the calendar menu opens over the card. */}
      <div
        ref={slotRef}
        className="relative z-10 mt-5 flex h-[5.375rem] w-full justify-center sm:h-[38px]"
      >
        {sealed ? (
          <button
            type="button"
            onClick={open}
            className="font-label text-xs font-medium uppercase tracking-[0.3em] text-gray-200 hover:text-white"
          >
            Tap to open
          </button>
        ) : (
          // Arrives with the card: same start, length and curve as the
          // card coming forward.
          <AddToCalendar
            enterDelayMs={T.settle.start}
            enterMs={T.settle.dur}
            easing={EASE.settle}
          />
        )}
      </div>
    </div>
  )
}
